import React, { RefObject, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ComboboxContent } from '../combobox-content';
import { ComboboxTrigger } from '../combobox-trigger';

import { ComboboxProps, ComboboxSelectionMode, ComboboxValue, ComboboxVariant } from './types';
import { Popover } from '@redsift/popovers';
import { ComboboxContext } from './context';
import {
  ListboxReducer,
  ListboxState,
  ListboxContextProps,
  FocusWithinGroup,
  ListboxContext,
  Flexbox,
  Text,
  ListboxActionType,
  Theme,
  useTheme,
  ThemeProvider,
  useId,
} from '@redsift/design-system';

const COMPONENT_NAME = 'Combobox';
const CLASSNAME = 'redsift-combobox';

/**
 * Combobox combines a text input with a filterable dropdown list.
 * Supports single or multiple selection with type-ahead filtering.
 *
 * Variants:
 * - `options`: Shows predefined options only (default)
 * - `freeform`: Allows custom values not in the list
 *
 * Selection modes: `single`, `multiple`
 *
 * @example
 * // Basic single-select combobox
 * <Combobox label="Country" onChange={(value) => setCountry(value)}>
 *   <Combobox.Trigger placeholder="Search countries..." />
 *   <Combobox.Content>
 *     <Item value="us">United States</Item>
 *     <Item value="uk">United Kingdom</Item>
 *     <Item value="ca">Canada</Item>
 *   </Combobox.Content>
 * </Combobox>
 *
 * @example
 * // Multi-select combobox
 * <Combobox
 *   label="Tags"
 *   selectionMode="multiple"
 *   value={selectedTags}
 *   onChange={setSelectedTags}
 * >
 *   <Combobox.Trigger placeholder="Add tags..." />
 *   <Combobox.Content>
 *     <Item value="urgent">Urgent</Item>
 *     <Item value="bug">Bug</Item>
 *     <Item value="feature">Feature</Item>
 *   </Combobox.Content>
 * </Combobox>
 *
 * @example
 * // Open dropdown on focus (for autocomplete fields)
 * <Combobox label="Assignee">
 *   <Combobox.Trigger openOnFocus placeholder="Search users..." />
 *   <Combobox.Content>...</Combobox.Content>
 * </Combobox>
 *
 * @example
 * // With Header and Footer
 * <Combobox label="Recipients">
 *   <Combobox.Trigger />
 *   <Combobox.Content>
 *     <Combobox.Content.Header>
 *       <Text variant="small">Recent contacts</Text>
 *     </Combobox.Content.Header>
 *     <Combobox.Content.Listbox>
 *       <Item value="alice">Alice</Item>
 *       <Item value="bob">Bob</Item>
 *     </Combobox.Content.Listbox>
 *     <Combobox.Content.Footer>
 *       <Button variant="text">Add new contact</Button>
 *     </Combobox.Content.Footer>
 *   </Combobox.Content>
 * </Combobox>
 *
 * @example
 * // Freeform variant (allows custom entries)
 * <Combobox label="Email" variant="freeform">
 *   <Combobox.Trigger placeholder="Enter or select email" />
 *   <Combobox.Content>
 *     {suggestions.map(email => <Item key={email} value={email}>{email}</Item>)}
 *   </Combobox.Content>
 * </Combobox>
 *
 * @example
 * // Multi-select with custom pill content
 * <Combobox selectionMode="multiple" variant="options">
 *   <Combobox.Trigger
 *     renderPillContent={(value) => (
 *       <Flexbox alignItems="center" gap="4px">
 *         <Icon icon={icons[value]} size="small" />
 *         <Text color={colors[value]}>{value}</Text>
 *       </Flexbox>
 *     )}
 *   >
 *     <TextField label="Pick items" />
 *   </Combobox.Trigger>
 *   <Combobox.Content>
 *     <Item value="cat">Cat</Item>
 *     <Item value="dog">Dog</Item>
 *   </Combobox.Content>
 * </Combobox>
 */
export const BaseCombobox: React.FC<ComboboxProps> & {
  displayName?: string;
  className?: string;
} = (props) => {
  const {
    defaultValue,
    description,
    descriptionProps,
    filter = {
      type: 'startsWith',
      caseSensitive: false,
    },
    formRef,
    submitRef,
    inputValue: propsInputValue,
    isDisabled,
    isInvalid,
    maxOptionsLength,
    minWidth = 'trigger-width',
    onChange,
    onInputChange,
    selectionMode = ComboboxSelectionMode.single,
    theme: propsTheme,
    triggerClassName,
    value,
    variant = ComboboxVariant.options,
    wrapperProps,
  } = props;

  const theme = useTheme(propsTheme);

  const [selectedValue, setValue] = useState<ComboboxValue>(
    value || defaultValue || (selectionMode === 'multiple' ? [] : '')
  );
  const [inputValue, setInputValue] = useState<string>(propsInputValue || '');
  useEffect(() => {
    if (value) {
      state.setValue(value);
    }
  }, [value]);
  useEffect(() => {
    if (propsInputValue) {
      state.setInputValue(propsInputValue);
    }
  }, [propsInputValue]);

  /** Listbox context */
  const [listboxState, listboxDispatch] = useReducer(ListboxReducer, {
    isDisabled,
    selectedValues: selectionMode === ComboboxSelectionMode.multiple ? selectedValue : [selectedValue],
    selectionMode,
  } as ListboxState);
  const listboxContext = useMemo<ListboxContextProps>(
    () => ({
      state: listboxState,
      dispatch: listboxDispatch,
    }),
    [listboxState]
  );
  useEffect(() => {
    listboxDispatch({
      type: ListboxActionType.UPDATE_OPTIONS,
      payload: {
        isDisabled: isDisabled || false,
      },
    });
  }, [isDisabled]);

  const freeTextItemRef = useRef<HTMLDivElement>();
  const [freeTextItemId] = useId();

  /** Combobox context. */
  const state = {
    filter,
    formRef,
    submitRef,
    value: selectedValue,
    inputValue,
    isDisabled: isDisabled || false,
    isInvalid: isInvalid || false,
    selectionMode,
    variant,
    freeTextItemRef: freeTextItemRef as RefObject<HTMLDivElement>,
    freeTextItemId,
    triggerClassName,
    setValue(value: ComboboxValue) {
      if (isDisabled) {
        return;
      }

      const previousValue = selectedValue;
      setValue(value);
      if (value !== previousValue && onChange) {
        onChange(value);
      }
    },
    setInputValue(value: string) {
      if (isDisabled) {
        return;
      }

      const previousValue = inputValue;
      setInputValue(value);
      if (value !== previousValue && onInputChange) {
        onInputChange(value);
      }
    },
  };

  return (
    <ThemeProvider value={{ theme }}>
      <FocusWithinGroup focusType="virtual-focus" focusOnInit={false} maxOptionsLength={maxOptionsLength}>
        <ComboboxContext.Provider value={state}>
          <ListboxContext.Provider value={listboxContext}>
            <Flexbox flexDirection="column" alignItems="flex-start" gap="0px" {...wrapperProps}>
              <Popover
                theme={theme}
                overrideDisplayName={{ content: 'ComboboxContent', trigger: 'ComboboxTrigger' }}
                placement="bottom-start"
                role="listbox"
                minWidth={minWidth}
                {...props}
              />
              {description && typeof description === 'string' ? (
                <Text
                  theme={theme}
                  marginTop="8px"
                  variant="caption"
                  color={isInvalid ? 'error' : theme === Theme.light ? 'dark-grey' : 'white'}
                  {...descriptionProps}
                >
                  {description}
                </Text>
              ) : React.isValidElement(description) ? (
                description
              ) : null}
            </Flexbox>
          </ListboxContext.Provider>
        </ComboboxContext.Provider>
      </FocusWithinGroup>
    </ThemeProvider>
  );
};
BaseCombobox.className = CLASSNAME;
BaseCombobox.displayName = COMPONENT_NAME;

export const Combobox = Object.assign(BaseCombobox, {
  Trigger: ComboboxTrigger,
  Content: ComboboxContent,
});
